import type { WeeklyLearningGuideExportPayload } from './weeklyLearningGuideExcelExport';
import { openBlankTabWithAppTitle, openSameOriginWindow } from './appDocumentTitle';

const STORAGE_PREFIX = 'cp.weeklyLearningGuideA4Preview.v1:';
const STASH_LS_MAX = 6;

export type WeeklyLearningGuideA4PreviewPayloadV1 = {
  v: 1;
  exportData: WeeklyLearningGuideExportPayload;
  fileNameBase: string;
};

export type WeeklyLearningGuideA4PreviewAction = 'view' | 'pdf' | 'print';

export type OpenWeeklyLearningGuideA4PreviewFailureReason = 'stash_failed' | 'popup_blocked';

export type OpenWeeklyLearningGuideA4PreviewResult =
  | { ok: true }
  | { ok: false; reason: OpenWeeklyLearningGuideA4PreviewFailureReason };

function stashKey(id: string): string {
  return STORAGE_PREFIX + id;
}

function newStashId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

function localStorageSafe(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function sessionStorageSafe(tab?: Window | null): Storage | null {
  try {
    if (tab && !tab.closed) {
      return tab.sessionStorage;
    }
  } catch {
    /* cross-origin or blocked */
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function pruneOldStashEntries(): void {
  const ls = localStorageSafe();
  if (!ls) return;
  const keys: string[] = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.sort();
  while (keys.length > STASH_LS_MAX) {
    const oldest = keys.shift();
    if (oldest) ls.removeItem(oldest);
  }
}

function writeStash(key: string, serialized: string, targetTab?: Window | null): boolean {
  const session = sessionStorageSafe(targetTab);
  if (session) {
    try {
      session.setItem(key, serialized);
      return true;
    } catch {
      /* fall through */
    }
  }
  const ls = localStorageSafe();
  if (!ls) return false;
  try {
    pruneOldStashEntries();
    ls.setItem(key, serialized);
    return true;
  } catch {
    return false;
  }
}

function readStash(key: string): string | null {
  try {
    const fromSession = window.sessionStorage.getItem(key);
    if (fromSession) return fromSession;
  } catch {
    /* ignore */
  }
  const ls = localStorageSafe();
  if (!ls) return null;
  try {
    return ls.getItem(key);
  } catch {
    return null;
  }
}

function parsePayload(raw: string): WeeklyLearningGuideA4PreviewPayloadV1 | null {
  try {
    const parsed = JSON.parse(raw) as WeeklyLearningGuideA4PreviewPayloadV1;
    if (!parsed || parsed.v !== 1 || !parsed.exportData || typeof parsed.fileNameBase !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function stashWeeklyLearningGuideA4PreviewPayload(
  payload: WeeklyLearningGuideA4PreviewPayloadV1,
  targetTab?: Window | null
): string {
  const id = newStashId();
  const key = stashKey(id);
  const serialized = JSON.stringify(payload);
  if (writeStash(key, serialized, targetTab)) return id;
  return '';
}

export function readWeeklyLearningGuideA4PreviewPayload(id: string): WeeklyLearningGuideA4PreviewPayloadV1 | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const raw = readStash(stashKey(trimmed));
  if (!raw) return null;
  return parsePayload(raw);
}

export function buildWeeklyLearningGuideA4PreviewUrl(
  id: string,
  action: WeeklyLearningGuideA4PreviewAction = 'view'
): string {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const params = new URLSearchParams({ k: id, action });
  const path = `${basePath}/weekly-learning-guide-a4-preview?${params.toString()}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function openWeeklyLearningGuideA4PreviewTabPlaceholder(): Window | null {
  return openBlankTabWithAppTitle();
}

export function closeWeeklyLearningGuideA4PreviewTabPlaceholder(tab: Window | null | undefined): void {
  try {
    tab?.close();
  } catch {
    /* ignore */
  }
}

export function openWeeklyLearningGuideA4PreviewFailureMessage(
  reason: OpenWeeklyLearningGuideA4PreviewFailureReason
): string {
  if (reason === 'stash_failed') {
    return '주간학습 안내 미리보기 탭을 열 수 없습니다. 브라우저 저장 공간을 비운 뒤 다시 시도해 주세요.';
  }
  return '주간학습 안내 미리보기 탭이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해 주세요.';
}

export function openWeeklyLearningGuideA4PreviewInNewTab(
  payload: WeeklyLearningGuideA4PreviewPayloadV1,
  action: WeeklyLearningGuideA4PreviewAction,
  options?: { preOpenedWindow?: Window | null }
): OpenWeeklyLearningGuideA4PreviewResult {
  const preOpened = options?.preOpenedWindow ?? null;
  const id = stashWeeklyLearningGuideA4PreviewPayload(payload, preOpened);
  if (!id.trim()) {
    closeWeeklyLearningGuideA4PreviewTabPlaceholder(preOpened);
    return { ok: false, reason: 'stash_failed' };
  }
  const url = buildWeeklyLearningGuideA4PreviewUrl(id, action);
  if (preOpened) {
    try {
      if (preOpened.closed) {
        closeWeeklyLearningGuideA4PreviewTabPlaceholder(preOpened);
        return { ok: false, reason: 'popup_blocked' };
      }
      preOpened.location.replace(url);
      preOpened.focus?.();
      return { ok: true };
    } catch {
      closeWeeklyLearningGuideA4PreviewTabPlaceholder(preOpened);
      const fallback = openSameOriginWindow(url);
      if (!fallback) return { ok: false, reason: 'popup_blocked' };
      return { ok: true };
    }
  }
  const w = openSameOriginWindow(url);
  if (!w) return { ok: false, reason: 'popup_blocked' };
  return { ok: true };
}
